import * as Icon from "lucide-react";
import React from "react";
import { useLeaderboard } from "../hooks/useLeaderboard";
import { logger } from "../lib/log.utils";

const positionStyles = {
    1: {
        icon: "Trophy",
        bg: "bg-accent/25",
        border: "border-gold",
        text: "text-gold-gradient",
        label: "🥇 1st Place",
    },
    2: {
        icon: "Medal",
        bg: "bg-cream-soft",
        border: "border-cream-soft",
        text: "text-cream-strong",
        label: "🥈 2nd Place",
    },
    3: {
        icon: "Award",
        bg: "bg-muted",
        border: "border-border",
        text: "text-muted-foreground",
        label: "🥉 3rd Place",
    },
};

const CompetitionResults = () => {
    const { data: leaderboard, isLoading } = useLeaderboard();
    const results = React.useMemo(() => {
        if (!leaderboard) return [];

        const competitions = (leaderboard.competitions || []).map((c) => ({
            competition: c.competition,
            date: c.date || "",
            status: c.status || "past",
            winners: (c.winners || [])
                .slice()
                .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                .map((w) => ({
                    position: w.position ?? 0,
                    name: w.name,
                    course: w.course || "",
                })),
        }));

        const upcoming = (leaderboard.upcomingEvents || []).map((ev) => ({
            competition: ev.title,
            date: ev.date || "",
            status: "upcoming",
            winners: [],
        }));

        return [...competitions, ...upcoming];
    }, [leaderboard]);

    if (isLoading) {
        return (
            <section className="py-24 px-4 bg-background" id="leaderboard">
                <div className="max-w-6xl mx-auto">Loading leaderboard...</div>
            </section>
        );
    }

    return (
        <section className="py-24 px-4 bg-background" id="leaderboard">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-16">
                    <p className="text-secondary font-heading text-lg tracking-widest uppercase mb-2">
                        Achievements
                    </p>
                    <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
                        {leaderboard?.title || "🏆 Competition Leaderboard"}
                    </h2>
                    {leaderboard?.description && (
                        <p className="text-muted-foreground font-body max-w-2xl mx-auto mb-4">
                            {leaderboard.description}
                        </p>
                    )}
                    <div className="w-24 h-1 bg-accent mx-auto rounded-full" />
                </div>

                <div className="space-y-10">
                    {results.length === 0 && (
                        <p className="text-muted-foreground font-body">
                            No leaderboard entries yet.
                        </p>
                    )}

                    {results.map((result, idx) => {
                        const isUpcoming = result.status === "upcoming";

                        return (
                            <div
                                key={`${result.competition}-${idx}`}
                                className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-sm"
                            >
                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
                                    <div>
                                        <h3 className="font-display text-2xl font-bold text-foreground flex items-center gap-2">
                                            <Icon.Trophy className="w-6 h-6 text-accent" />
                                            {result.competition}
                                        </h3>
                                        {result.date && (
                                            <p className="text-sm text-muted-foreground font-body flex items-center gap-1 mt-1">
                                                <Icon.Calendar className="w-3 h-3" />
                                                {result.date}
                                            </p>
                                        )}
                                    </div>
                                    <span
                                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                                            isUpcoming
                                                ? "bg-accent/10 text-accent"
                                                : "bg-emerald-50 text-emerald-700"
                                        }`}
                                    >
                                        <span
                                            className={`w-2 h-2 rounded-full ${
                                                isUpcoming ? "bg-accent" : "bg-emerald-500"
                                            }`}
                                        />
                                        {isUpcoming ? "Upcoming" : "Past"}
                                    </span>
                                </div>

                                {result.winners.length === 0 ? (
                                    <p className="text-sm text-muted-foreground font-body">
                                        {isUpcoming
                                            ? "Results will be updated after the competition."
                                            : "No winners recorded yet for this competition."}
                                    </p>
                                ) : (
                                    <div className="space-y-3">
                                        {result.winners.map((w) => {
                                            const style =
                                                positionStyles[w.position] || positionStyles[3];
                                            const IconComponent = Icon[style.icon];

                                            return (
                                                <div
                                                    key={`${result.competition}-${w.position}-${w.name}`}
                                                    className={`flex items-center gap-4 p-3 rounded-lg border ${style.bg} ${style.border} transition-colors`}
                                                >
                                                    <div className="w-10 h-10 rounded-full bg-card flex items-center justify-center shrink-0">
                                                        {IconComponent &&
                                                            React.createElement(IconComponent, {
                                                                className: `w-5 h-5 ${style.text}`,
                                                            })}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p
                                                            className={`font-display font-semibold text-sm md:text-base truncate ${style.text}`}
                                                        >
                                                            {w.name}
                                                        </p>
                                                        {w.course && (
                                                            <p className="text-xs text-muted-foreground font-body">
                                                                {w.course}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="text-right shrink-0">
                                                        <p className="text-xs font-body font-medium text-muted-foreground whitespace-nowrap">
                                                            {style.label}
                                                        </p>
                                                        <p className="text-[11px] text-muted-foreground">
                                                            Position {w.position}
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default CompetitionResults;
